import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MapPin, Loader2 } from "lucide-react";
import { API } from "@/lib/api";

// Free address autocomplete: OSM Nominatim (all of Europe) + Gemini AI fallback
// for obscure POIs. Curated Riviera picks stay on top. Selecting a suggestion
// keeps the FULL structured address + coordinates so the price calculator and
// the form always agree on the same trip.

const NOMINATIM = "https://nominatim.openstreetmap.org/search";
const COUNTRY_CODES = "fr,mc,it,es,pt,de,at,ch,be,nl,lu,gb,ie,dk,se,no,fi,is,pl,cz,sk,hu,ro,bg,hr,si,gr,ee,lv,lt,cy,mt,ad,sm,va";
// Riviera bias box (lon/lat): left,top,right,bottom
const RIVIERA_VIEWBOX = "6.5,44.2,7.8,43.2";

// Case + diacritic insensitive normalization
const norm = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

// Curated quick-pick suggestions with coordinates [lat, lon] (zero-latency)
const CURATED = [
  { label: "Aéroport Nice Côte d'Azur (Terminal 1)", value: "Aéroport Nice Côte d'Azur, Terminal 1, 06200 Nice, France", lat: 43.6606, lon: 7.2122 },
  { label: "Aéroport Nice Côte d'Azur (Terminal 2)", value: "Aéroport Nice Côte d'Azur, Terminal 2, 06200 Nice, France", lat: 43.6659, lon: 7.2169 },
  { label: "Gare SNCF Nice-Ville", value: "Gare de Nice-Ville, Avenue Thiers, 06000 Nice, France", lat: 43.7044, lon: 7.2619 },
  { label: "Port de Nice", value: "Port de Nice, Quai Cassini, 06300 Nice, France", lat: 43.6951, lon: 7.2865 },
  { label: "Casino de Monte-Carlo, Monaco", value: "Place du Casino, 98000 Monaco", lat: 43.7394, lon: 7.4280 },
  { label: "Hôtel de Paris Monte-Carlo", value: "Hôtel de Paris, Place du Casino, 98000 Monaco", lat: 43.7390, lon: 7.4287 },
  { label: "Palais des Festivals, Cannes", value: "Palais des Festivals, 1 Boulevard de la Croisette, 06400 Cannes, France", lat: 43.5513, lon: 7.0174 },
  { label: "Hôtel Carlton, Cannes", value: "Carlton Cannes, 58 Boulevard de la Croisette, 06400 Cannes, France", lat: 43.5503, lon: 7.0245 },
  { label: "Hôtel Negresco, Nice", value: "Hôtel Negresco, 37 Promenade des Anglais, 06000 Nice, France", lat: 43.6947, lon: 7.2589 },
  { label: "Cap d'Antibes - Hôtel du Cap-Eden-Roc", value: "Hôtel du Cap-Eden-Roc, Boulevard J.F. Kennedy, 06160 Antibes, France", lat: 43.5520, lon: 7.1230 },
  { label: "Gare de Cannes", value: "Gare de Cannes, Rue Jean Jaurès, 06400 Cannes, France", lat: 43.5538, lon: 7.0199 },
  { label: "Port Hercule, Monaco", value: "Port Hercule, 98000 Monaco", lat: 43.7347, lon: 7.4258 },
  { label: "Saint-Paul-de-Vence (centre)", value: "Saint-Paul-de-Vence, 06570, France", lat: 43.6950, lon: 7.1220 },
  { label: "Èze Village", value: "Èze Village, 06360 Èze, France", lat: 43.7276, lon: 7.3620 },
];

const filterCurated = (q) => {
  const lower = norm(q);
  if (!lower) return CURATED.slice(0, 6);
  return CURATED.filter((c) => norm(c.label).includes(lower) || norm(c.value).includes(lower)).slice(0, 6);
};

// Riviera = Alpes-Maritimes (06xxx), Monaco, Var coast — prioritized in results
const isRiviera = (d) => {
  const addr = d.address || {};
  const pc = addr.postcode || "";
  const cc = (addr.country_code || "").toLowerCase();
  if (cc === "mc") return true;
  if (cc === "fr" && (pc.startsWith("06") || pc.startsWith("83"))) return true;
  const dn = norm(d.display_name || "");
  return ["nice", "monaco", "cannes", "antibes", "menton", "cote d'azur", "alpes-maritimes"].some((k) => dn.includes(k));
};

// Build a short, readable label: "73 Avenue de Grasse, 06800 Cagnes-sur-Mer"
// (number + street, then postcode + city) instead of Nominatim's long display_name.
const formatAddress = (d) => {
  const a = d.address || {};
  const city = a.city || a.town || a.village || a.municipality || a.hamlet || a.suburb || a.county || "";
  const street = [a.house_number, a.road].filter(Boolean).join(" ");
  const poi = d.name || a.tourism || a.aeroway || a.amenity || a.building || a.shop || a.leisure || a.office || "";
  const head = [];
  if (poi && norm(poi) !== norm(street)) head.push(poi);
  if (street) head.push(street);
  const line2 = [a.postcode, city].filter(Boolean).join(" ");
  const parts = [head.join(", "), line2].filter(Boolean);
  if (!parts.length) return (d.display_name || "").split(",").slice(0, 3).join(",").trim();
  const cc = (a.country_code || "").toLowerCase();
  if (cc && !["fr", "mc"].includes(cc) && a.country) parts.push(a.country);
  return parts.join(", ");
};

const AddressAutocomplete = ({ value, onChange, onSelect, placeholder, testid, label, required, icon: Icon = MapPin, inputClassName = "" }) => {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!value || value.length < 2) {
      // Keep the search box empty until the user types — no pre-filled suggestions
      setResults([]);
      setLoading(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      try {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        setLoading(true);

        const params = new URLSearchParams({
          q: value,
          format: "json",
          addressdetails: "1",
          limit: "8",
          countrycodes: COUNTRY_CODES,
          viewbox: RIVIERA_VIEWBOX,
          bounded: "0",
          "accept-language": "fr",
        });

        const res = await fetch(`${NOMINATIM}?${params}`, {
          signal: abortRef.current.signal,
          headers: { "Accept": "application/json" },
        });
        const data = await res.json();

        // Riviera results first, then the rest (stable within each group)
        const sorted = [...data].sort((a, b) => (isRiviera(b) ? 1 : 0) - (isRiviera(a) ? 1 : 0));
        const fromApi = sorted.map((d) => {
          const short = formatAddress(d);
          return {
            label: short,
            value: short,
            lat: parseFloat(d.lat),
            lon: parseFloat(d.lon),
            riviera: isRiviera(d),
            curated: false,
          };
        });

        // AI fallback for exotic POIs when Nominatim finds nothing
        let aiExtra = [];
        if (fromApi.length === 0 && value.trim().length >= 3) {
          try {
            const aiRes = await axios.get(`${API}/ai/address-suggest`, {
              params: { q: value },
              signal: abortRef.current.signal,
            });
            const list = Array.isArray(aiRes.data?.suggestions) ? aiRes.data.suggestions : [];
            aiExtra = list.map((s) => ({ label: s.label, value: s.full, lat: null, lon: null, curated: false, ai: true }));
          } catch (_) { /* silent */ }
        }

        const curated = filterCurated(value).map((c) => ({ label: c.label, value: c.value, lat: c.lat, lon: c.lon, curated: true }));
        const seen = new Set(curated.map((c) => norm(c.label)));
        const merged = [...curated, ...fromApi.filter((r) => !seen.has(norm(r.label))), ...aiExtra].slice(0, 10);
        setResults(merged);
      } catch (err) {
        if (err.name !== "AbortError") {
          setResults(filterCurated(value).map((c) => ({ label: c.label, value: c.value, lat: c.lat, lon: c.lon, curated: true })));
        }
      } finally {
        setLoading(false);
      }
    }, 350);
  }, [value, open]);

  const handleSelect = (item) => {
    onChange(item.value);
    if (onSelect) {
      onSelect({
        value: item.value,
        label: item.label,
        coords: item.lat != null && item.lon != null ? [item.lat, item.lon] : null,
      });
    }
    setOpen(false);
  };

  const baseInput = "w-full bg-[#0F1115] border border-white/10 focus:border-[#D4AF37] rounded-xl pl-10 pr-4 py-3 text-white placeholder-white/30 text-sm outline-none transition-colors";

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Icon size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#D4AF37]" />
        <input
          id={testid}
          data-testid={testid}
          type="text"
          required={required}
          value={value}
          onFocus={() => setOpen(true)}
          onChange={(e) => { onChange(e.target.value); if (onSelect) onSelect({ value: e.target.value, label: e.target.value, coords: null }); setOpen(true); }}
          placeholder={placeholder}
          autoComplete="off"
          aria-label={label || placeholder}
          className={`${baseInput} ${inputClassName}`}
        />
        {loading && (
          <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#D4AF37] animate-spin" />
        )}
      </div>

      {open && results.length > 0 && (
        <div
          data-testid={`${testid}-dropdown`}
          className="absolute z-50 left-0 right-0 mt-2 bg-[#0F1115] border border-white/10 rounded-xl shadow-2xl overflow-hidden max-h-72 overflow-y-auto"
        >
          {results.map((r, i) => (
            <button
              key={i}
              type="button"
              data-testid={`${testid}-option-${i}`}
              onClick={() => handleSelect(r)}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] transition-colors flex items-start gap-2.5 border-b border-white/5 last:border-0"
            >
              <MapPin size={13} className={`mt-0.5 shrink-0 ${r.curated || r.riviera ? "text-[#D4AF37]" : r.ai ? "text-emerald-400" : "text-white/40"}`} />
              <div className="flex-1 min-w-0">
                <div className="text-white/90 truncate">{r.label}</div>
                {r.curated && <div className="text-[10px] text-[#D4AF37]/60 mt-0.5 uppercase tracking-wider">Suggéré</div>}
                {!r.curated && r.riviera && <div className="text-[10px] text-[#D4AF37]/60 mt-0.5 uppercase tracking-wider">Côte d'Azur</div>}
                {r.ai && <div className="text-[10px] text-emerald-400/70 mt-0.5 uppercase tracking-wider">IA</div>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
